<script>
import _ from 'lodash'
import moment from 'moment'
import { isDateInRange, mergeOverlappingUnixDateIntervals } from '@utils/date-helpers'
import Tooltip from '@components/tooltip.vue'
import { authComputed } from '@state/helpers'
import { eventColorsMixin } from '@src/mixins/eventColorsMixin'

export default {
  components: {
    Tooltip,
  },
  mixins: [eventColorsMixin],
  props: {
    events: {
      type: Array,
      required: true,
    },
    selectedDateProp: {
      type: Date,
      default: null,
    },
  },

  data() {
    return {
      eventPriorityOrder: {
        'Network Event': 11,
        'Industry Events': 10,
        'Prof. Development/Education': 9,
        'Support Closure': 8,
        'Onboarding and Implementation': 7,
        'Internal Event': 6,
        'Communication Calendar': 5,
      },
      calendarOptions: {
        locale: {
          id: 'en-us',
          masks: { weekdays: 'WW' },
        },
      },
      calendarAttributes: [],
      selectedDate: null,
    }
  },
  computed: {
    ...authComputed,
    maxPriorityOrder() {
      return _.max(_.values(this.eventPriorityOrder))
    },
  },
  watch: {
    events: {
      immediate: true,
      handler() {
        this.calendarAttributes = [...this.getSingleDayEvents()]
      },
    },
    selectedDateProp: {
      immediate: true,
      handler(val) {
        this.selectedDate = val
      },
    },
  },
  created() {
    this.calendarAttributes = [...this.getSingleDayEvents()]
  },
  methods: {
    getSingleDayEvents() {
      return [
        ...this.getPopoverAttribute(),
        this.getHighlightAttribute('Network Event'),
        this.getHighlightAttribute('Industry Events'),
        this.getHighlightAttribute('Prof. Development/Education'),
        this.getHighlightAttribute('Support Closure'),
        this.getHighlightAttribute('Onboarding and Implementation'),
        this.getHighlightAttribute('Internal Event'),
        this.getHighlightAttribute('Communication Calendar'),
        this.getDotAttribute('Network Event'),
        this.getDotAttribute('Industry Events'),
        this.getDotAttribute('Prof. Development/Education'),
        this.getDotAttribute('Support Closure'),
        this.getDotAttribute('Onboarding and Implementation'),
        this.getDotAttribute('Internal Event'),
        this.getDotAttribute('Communication Calendar'),
      ]
    },

    getHighlightAttribute(eventType) {
      const order = this.getEventTypeOrder(eventType)
      const events = _(this.events)
        .filter({
          eventType,
        })
        .value()
      const dateUnixIntervals = [
        ...events.map((event) => {
          if (event.shouldGroupBeBubbled) {
            return [
              moment(event.groupStartDate, 'YYYY-MM-DD hh:mm:ss').startOf('day').unix(),
              moment(event.groupEndDate, 'YYYY-MM-DD hh:mm:ss').startOf('day').unix(),
            ]
          }
          return [
            moment(event.start, 'YYYY-MM-DD hh:mm:ss').startOf('day').unix(),
            moment(event.end, 'YYYY-MM-DD hh:mm:ss').startOf('day').unix(),
          ]
        }),
      ]

      const eventColorClass = this.getEventColorClass(eventType)
      return {
        highlight: {
          fillMode: 'solid',
          class: `calendar-highlight-date ${eventColorClass}`,
          contentClass: 'calendar-highlight-content',
        },
        dates: mergeOverlappingUnixDateIntervals(dateUnixIntervals).map((dateUnixInterval) => ({
          start: moment.unix(dateUnixInterval[0]).toDate(),
          end: moment.unix(dateUnixInterval[1]).toDate(),
        })),
        order,
      }
    },
    getDotAttribute(eventType) {
      const order = this.getEventTypeOrder(eventType)

      const events = _.filter(this.events, {
        eventType,
      })
      const eventColorClass = this.getEventColorClass(eventType)
      const dates = _.flatten(events.map((event) => this.getDotDates(event)))
      return {
        dot: {
          class: `calendar-dot-date ${eventColorClass}`,
        },
        dates,
        order,
      }
    },

    getPopoverAttribute() {
      const orderedEvents = _.orderBy(this.events, ({ start }) => moment(start, 'YYYY-MM-DD hh:mm:ss').toDate())
      return orderedEvents.map((event) => {
        return {
          dates: {
            start: moment(event.start, 'YYYY-MM-DD hh:mm:ss').toDate(),
            end: moment(event.end, 'YYYY-MM-DD hh:mm:ss').toDate(),
          },
          popover: {
            label: event.title,
            color: this.getEventColorHex(event.eventType),
          },
        }
      })
    },

    getEventTypeOrder(eventType) {
      return this.eventPriorityOrder[eventType]
    },

    getDotDates(dotEvent) {
      const otherEvents = this.events.filter((event) => event.eventId !== dotEvent.eventId)
      const events = []

      for (let i = 0; i < dotEvent.eventDurationInDays; i++) {
        const dateToCompare = moment(dotEvent.start, 'YYYY-MM-DD hh:mm:ss').add(i, 'days').toDate()
        const eventsOnThisDate = _(otherEvents)
          .filter(({ startDateObject, endDateObject }) => isDateInRange(dateToCompare, startDateObject, endDateObject))
          .value()

        const shouldAddDot = eventsOnThisDate.some(
          (event) => this.getEventTypeOrder(dotEvent.eventType) <= this.getEventTypeOrder(event.eventType)
        )

        if (shouldAddDot) {
          events.push(moment(dateToCompare, 'YYYY-MM-DD hh:mm:ss').toDate())
        }
      }
      return events
    },

    updateDate() {
      this.$emit('updateDate', this.selectedDate)
    },
  },
}
</script>

<template>
  <div class="position-relative">
    <v-date-picker
      v-model="selectedDate"
      class="motto-calendar"
      :attributes="calendarAttributes"
      :locale="calendarOptions.locale"
      :value="null"
      color="blue"
      @dayclick="updateDate" />
    <Tooltip target="tooltip-button-show-event" placement="top" custom-class="calendar-tooltip">
      <div class="tooltip-item">
        <div class="calendar-tooltip-color-example calendar-blue" />
        <p class="overline-text">Network Events</p>
      </div>
      <div class="tooltip-item">
        <div class="calendar-tooltip-color-example calendar-green" />
        <p class="overline-text">Industry Events</p>
      </div>
      <div class="tooltip-item">
        <div class="calendar-tooltip-color-example calendar-orange" />
        <p class="overline-text">Prof. Development/Education</p>
      </div>
      <div class="tooltip-item">
        <div class="calendar-tooltip-color-example calendar-gray" />
        <p class="overline-text">Support Closure</p>
      </div>
      <div class="tooltip-item">
        <div class="calendar-tooltip-color-example calendar-dark-blue" />
        <p class="overline-text">Onboarding and Implementation</p>
      </div>
      <template v-if="isMottoCorporate">
        <div class="tooltip-item">
          <div class="calendar-tooltip-color-example calendar-slate" />
          <p class="overline-text">Internal Event</p>
        </div>
        <div class="tooltip-item">
          <div class="calendar-tooltip-color-example calendar-red" />
          <p class="overline-text">Communication Calendar</p>
        </div>
      </template>
    </Tooltip>
  </div>
</template>

<style lang="scss">
@import '@design';

.motto-calendar {
  width: 100%;
  border: none;

  .vc-title {
    font-size: 18px;
    font-weight: 700;
    color: $primary-blue;
    pointer-events: none;
  }

  .vc-arrows-container {
    right: 50%;
    left: 50%;
    width: 75%;
    transform: translateX(-50%);

    .vc-arrow {
      width: 32px;
      height: 32px;
      background: url(../../assets/icons/ArrowGray.svg) no-repeat center;
      border: 1px solid $light-gray;
      border-radius: 50%;

      svg {
        display: none;
      }

      &.is-right {
        display: block;
        transform: rotate(180deg);
        transform-origin: center center;
      }
    }
  }

  .vc-weeks {
    margin-top: 27px;

    .vc-weekday {
      font-size: 12px;
      font-weight: 700;
      color: $primary-blue;
    }

    .vc-day {
      margin-bottom: 10px;
    }
  }

  .vc-popover-content {
    @media (max-width: $lg-breakpoint) {
      display: none;
    }

    background: $primary-blue;
  }

  .vc-focusable {
    border-radius: 8px;

    &:not(.calendar-highlight-content) {
      color: $dark-gray !important;
    }
  }

  .vc-highlight {
    &:not(.calendar-highlight-date) {
      background-color: transparent !important;
      border: 2px solid $primary-blue;
    }
  }

  [class='vc-highlight'] {
    z-index: 1;
    border: 1px solid $primary-blue;
  }

  .vc-day-content:focus {
    font-weight: unset;
    background: transparent !important;
  }
}
</style>
